import {
  ID,
  newTaskSuffix,
  noDeadlineSectionID,
  overdueSectionID,
  rootId,
  SortingMode,
  Task,
  TaskListItemTypes,
  TaskListTreeItem,
  Timestamp,
  WeekDays,
} from '@griply/types';
import { isBefore } from 'date-fns';

import { formatDateKey, parseDateKey } from '../format-date-key';
import { getSortedTasks } from '../get-sorted-tasks';

export type BuildDeadlineDeadlineTreeOptions = {
  tasks: Task[];
  customOrder?: Record<ID, ID[]>;
  sortingMode?: SortingMode | null;
  today: Timestamp;
  weekStartsOn: WeekDays;
  showEmptyGroups?: boolean;
  hideNewTaskRow?: boolean;
  overdueLabel: string;
  noDeadlineLabel: string;
  rootLabel: string;
  newTaskLabel: string;
};

export const buildDeadlineDeadlineTree = ({
  tasks,
  customOrder,
  sortingMode,
  today,
  weekStartsOn,
  showEmptyGroups,
  hideNewTaskRow,
  overdueLabel,
  noDeadlineLabel,
  rootLabel,
  newTaskLabel,
}: BuildDeadlineDeadlineTreeOptions): Record<ID, TaskListTreeItem> => {
  const dateSections = Array.from(
    tasks.reduce<Set<ID>>((acc, task) => {
      if (!task.deadlineDeadline) {
        acc.add(noDeadlineSectionID);
        return acc;
      }
      if (isBefore(task.deadlineDeadline, today)) {
        acc.add(overdueSectionID);
        return acc;
      }
      const dateKey = formatDateKey(task.deadlineDeadline!);
      acc.add(dateKey);
      return acc;
    }, new Set<ID>()),
  );

  if (showEmptyGroups) {
    if (!dateSections.includes(noDeadlineSectionID)) {
      dateSections.push(noDeadlineSectionID);
    }
    if (!dateSections.includes(overdueSectionID)) {
      dateSections.push(overdueSectionID);
    }
  }

  dateSections.sort((a, b) =>
    a === overdueSectionID
      ? -1
      : b === overdueSectionID
        ? 1
        : a === noDeadlineSectionID
          ? 1
          : b === noDeadlineSectionID
            ? -1
            : parseDateKey(a).getTime() - parseDateKey(b).getTime(),
  );

  const customTaskOrder =
    sortingMode === SortingMode.Custom
      ? dateSections
          .map((dateSection) => customOrder?.[dateSection] ?? [])
          .flat()
      : [];

  const treeBase: Record<ID, TaskListTreeItem> = showEmptyGroups
    ? {
        [overdueSectionID]: {
          id: overdueSectionID,
          type: TaskListItemTypes.Date,
          name: overdueLabel,
          children: [],
          isFolder: true,
        },
      }
    : {};

  const allTreeItems = getSortedTasks(tasks, {
    today,
    weekStartsOn,
    order: customTaskOrder,
    mode: sortingMode ? [SortingMode.DueDate, sortingMode] : undefined,
    allDayFirst: true,
  }).reduce<Record<ID, TaskListTreeItem>>((acc, task) => {
    if (!task.deadlineDeadline) {
      acc[noDeadlineSectionID] = acc[noDeadlineSectionID] ?? {
        id: noDeadlineSectionID,
        type: TaskListItemTypes.Date,
        name: noDeadlineLabel,
        children: [],
        isFolder: true,
      };
      acc[noDeadlineSectionID].children?.push(task.id);
      acc[task.id] = {
        id: task.id,
        name: task.name,
        type: TaskListItemTypes.Task,
      };
      return acc;
    }

    if (isBefore(task.deadlineDeadline, today)) {
      acc[overdueSectionID] = acc[overdueSectionID] ?? {
        id: overdueSectionID,
        type: TaskListItemTypes.Date,
        name: overdueLabel,
        children: [],
        isFolder: true,
      };
      acc[overdueSectionID].children?.push(task.id);
      acc[task.id] = {
        id: task.id,
        name: task.name,
        type: TaskListItemTypes.Task,
      };
      return acc;
    }

    const dateKey = formatDateKey(task.deadlineDeadline!);
    acc[dateKey] = acc[dateKey] ?? {
      id: dateKey,
      type: TaskListItemTypes.Date,
      name: formatDateKey(task.deadlineDeadline!),
      children: [],
      isFolder: true,
    };
    acc[dateKey].children?.push(task.id);
    acc[task.id] = {
      id: task.id,
      name: task.name,
      type: TaskListItemTypes.Task,
    };

    return acc;
  }, treeBase);

  if (showEmptyGroups) {
    allTreeItems[noDeadlineSectionID] = allTreeItems[noDeadlineSectionID] ?? {
      id: noDeadlineSectionID,
      type: TaskListItemTypes.Date,
      name: noDeadlineLabel,
      children: [],
      isFolder: true,
    };
  }

  if (!hideNewTaskRow) {
    dateSections.forEach((dateSectionKey) => {
      if (dateSectionKey === overdueSectionID) {
        return;
      }

      const newTaskItemId = `${dateSectionKey}${newTaskSuffix}`;
      allTreeItems[dateSectionKey]?.children?.push(newTaskItemId);
      allTreeItems[newTaskItemId] = {
        id: newTaskItemId,
        name: newTaskLabel,
        type: TaskListItemTypes.NewTask,
      };
    });
  }

  return {
    [rootId]: {
      id: rootId,
      name: rootLabel,
      type: TaskListItemTypes.Root,
      children: dateSections,
    },
    ...allTreeItems,
  };
};
